<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {useNow} from "@vueuse/core";
import {CalendarClock, CalendarPlus} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import AgendaPatientSmsOption from "@/agenda/components/AgendaPatientSmsOption.vue";
import MedicationCheckupAppointmentDialog from "@/medication-checkup/components/MedicationCheckupAppointmentDialog.vue";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";
import {DEFAULT_APPOINTMENT_COLOR} from "@/agenda/constants";
import {
    emptyPatientSmsChoice,
    isPatientSmsChoiceValid,
    PatientSmsChoice,
    patientSmsOutcome,
    patientSmsRequest
} from "@/agenda/lib/patientSms";
import {appointmentApi} from "@/container";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {useToastStore} from "@/stores/toast.store";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import {NextAppointmentView} from "@/medication-checkup/interfaces/next-appointment";
import {CARD_SHEET} from "@/medication-checkup/services/SceneClasses";

/**
 * The appointment of the step that comes next with the patient, as the
 * agenda holds it.
 *
 * Not a record: it stays live once the block is completed, because the
 * appointment lives in the agenda and keeps moving there. Booking, moving and
 * canceling are agenda calls, never submissions of the block; each one reads
 * the cycle again, which is what serves the appointment back.
 *
 * Three states: nothing booked (the suggested day, and booking), an
 * appointment ahead (moving or canceling it), an appointment held (shown,
 * and booking again). Once the planned step is held, it is shown only.
 */
const props = defineProps<{view: NextAppointmentView}>();

const store = useMedicationCheckupStore();
const toastStore = useToastStore();

/**
 * Followed rather than captured: the card stays on screen while the
 * appointment it shows starts and ends.
 */
const now = useNow({interval: 60 * 1000});

const appointment = computed(() => props.view.appointment);

const plannedStep = computed(() => store.steps.find(step => step.id === props.view.planned_step_id) ?? null);

const plannedStepLabel = computed(() => plannedStep.value
    ? store.declaredStep(plannedStep.value.definition_key)?.label ?? 'Prochaine étape'
    : 'Prochaine étape');

/**
 * A step already held has nothing left to plan: reread later, the card only
 * shows the appointment it was held on.
 */
const plannedStepHeld = computed(() => plannedStep.value?.status === 'completed');

/** An appointment over is shown as held: there is nothing left to move. */
const held = computed(() => appointment.value !== null
    && parseAgendaDate(appointment.value.end_at).isBefore(now.value));

const when = computed(() => appointment.value
    ? parseAgendaDate(appointment.value.start_at).format('dddd D MMMM [à] HH:mm')
    : null);

const minutes = computed(() => appointment.value
    ? parseAgendaDate(appointment.value.end_at).diff(parseAgendaDate(appointment.value.start_at), 'minute')
    : null);

const member = computed(() => appointment.value?.operator_access_point?.name ?? 'Sans intervenant');

const suggested = computed(() => parseAgendaDate(props.view.suggested_on).format('dddd D MMMM'));

const dialogOpen = ref(false);
const moved = ref(false);

const plan = () => {
    moved.value = false;
    dialogOpen.value = true;
};

const move = () => {
    moved.value = true;
    dialogOpen.value = true;
};

const cancelOpen = ref(false);
const cancelSms = ref<PatientSmsChoice>(emptyPatientSmsChoice());
const canceling = ref(false);

watch(cancelOpen, (isOpen) => {
    if (isOpen) {
        cancelSms.value = emptyPatientSmsChoice();
    }
});

/**
 * Cancel the appointment once confirmed, texting the patient only when asked
 * for. The step is then planned on nothing, and the card offers booking again.
 */
const cancel = async () => {
    const canceled = appointment.value;
    const sms = cancelSms.value;

    if (canceled === null) {
        return;
    }

    canceling.value = true;

    try {
        const {patientNotification} = await appointmentApi().cancelAppointment(canceled.id, patientSmsRequest(sms));

        toastStore.showSuccess({content: 'Rendez-vous annulé.'});

        const outcome = patientSmsOutcome(sms, patientNotification);

        if (outcome) {
            toastStore.showToast(outcome);
        }

        cancelOpen.value = false;
        await store.refreshCycle();
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }
    } finally {
        canceling.value = false;
    }
};
</script>

<template>
  <div :class="CARD_SHEET" class="max-w-[640px] rounded-xl overflow-hidden">
    <div class="flex items-center gap-3 px-4 py-3.5 bg-primary-lightest text-primary">
      <span class="w-9 h-9 shrink-0 rounded-full bg-white flex items-center justify-center">
        <CalendarClock class="w-5 h-5" />
      </span>
      <span class="flex flex-col">
        <span class="text-base font-bold">Prochain rendez-vous</span>
        <span class="text-sm text-gray-dark">{{ plannedStepLabel }}</span>
      </span>
    </div>

    <div class="flex flex-col gap-3 px-4 py-4">
      <template v-if="appointment">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-semibold text-gray-dark first-letter:uppercase">
            {{ held ? `Rendez-vous du ${when}, passé` : when }}
          </span>
          <span class="text-sm text-muted-foreground">
            {{ minutes }} min · {{ member }}
          </span>
          <span v-if="appointment.appointment_type" class="flex items-center gap-2 text-sm text-muted-foreground">
            <span
              class="size-2.5 shrink-0 rounded-full"
              :style="{backgroundColor: appointment.appointment_type.color ?? DEFAULT_APPOINTMENT_COLOR}"
            />
            {{ appointment.appointment_type.name }}
          </span>
        </div>

        <div v-if="!held && !plannedStepHeld" class="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" @click="move">
            Déplacer
          </Button>
          <Button variant="ghost" size="sm" class="text-red hover:text-red" @click="cancelOpen = true">
            Annuler le rendez-vous
          </Button>
        </div>
      </template>

      <span v-else-if="plannedStepHeld" class="text-sm text-muted-foreground">
        Aucun rendez-vous pris depuis le bilan.
      </span>

      <span v-else class="text-sm text-muted-foreground">
        Aucun rendez-vous prévu. Suggéré vers le {{ suggested }}.
      </span>

      <div v-if="(!appointment || held) && !plannedStepHeld">
        <Button
          size="sm"
          class="bg-primary hover:bg-primary/90"
          :disabled="view.planned_step_id === null"
          @click="plan"
        >
          <CalendarPlus />
          Prévoir le rendez-vous
        </Button>
      </div>
    </div>

    <MedicationCheckupAppointmentDialog
      v-if="view.planned_step_id !== null"
      v-model:open="dialogOpen"
      :planned-step-id="view.planned_step_id"
      :planned-step-label="plannedStepLabel"
      :suggested-on="view.suggested_on"
      :appointment="moved ? appointment : null"
      @saved="store.refreshCycle()"
    />

    <AlertDialog v-if="appointment" v-model:open="cancelOpen">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Annuler ce rendez-vous ?</AlertDialogTitle>
          <AlertDialogDescription>
            Le rendez-vous du {{ when }} sera annulé dans l'agenda.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AgendaPatientSmsOption v-model="cancelSms" :appointment-id="appointment.id" event="canceled" />
        <AlertDialogFooter>
          <AlertDialogCancel>Retour</AlertDialogCancel>
          <AlertDialogAction
            class="bg-red hover:bg-red/90"
            :disabled="canceling || !isPatientSmsChoiceValid(cancelSms)"
            @click="cancel"
          >
            Annuler le rendez-vous
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
